import { useState, useEffect, useRef, type ReactNode } from 'react'
import { activeProjectId } from '../shell/ownerEnv'
import { useNls } from '../../../nls/nlsProvider'
import './ownerKit.css'

/* ==========================================================================
   1. Soft Confirm (Inline toggle for 3 seconds)
   ========================================================================== */

export type OwnerSoftConfirmProps = {
  label: string
  onConfirm: () => void | Promise<void>
  disabled?: boolean
  className?: string
  timeoutMs?: number
}

export function OwnerSoftConfirm({
  label,
  onConfirm,
  disabled = false,
  className = '',
  timeoutMs = 3000,
}: OwnerSoftConfirmProps) {
  const { t } = useNls()
  const [confirming, setConfirming] = useState(false)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const handleInitialClick = () => {
    if (disabled) return
    setConfirming(true)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      setConfirming(false)
    }, timeoutMs)
  }

  const handleYes = async () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setConfirming(false)
    await onConfirm()
  }

  const handleNo = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setConfirming(false)
  }

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  if (confirming) {
    return (
      <div className={`ownerConfirmSoftWrap ${className}`} role="alert">
        <span className="ownerConfirmSoftPrompt">{t('NLSOwnerConsoleSure')}</span>
        <button
          type="button"
          className="ownerConfirmSoftBtn ownerConfirmSoftBtn--yes"
          onClick={() => void handleYes()}
        >
          {t('NLSOwnerConsoleYes')}
        </button>
        <button
          type="button"
          className="ownerConfirmSoftBtn ownerConfirmSoftBtn--no"
          onClick={handleNo}
        >
          {t('NLSOwnerConsoleNo')}
        </button>
      </div>
    )
  }

  return (
    <button
      type="button"
      className={`ownerModalBtn ownerModalBtn--default ${className}`}
      onClick={handleInitialClick}
      disabled={disabled}
    >
      {label}
    </button>
  )
}

/* ==========================================================================
   2. Firm Confirm (Modal dialog, requires >= 12 chars reason, single "Do it")
   ========================================================================== */

export type OwnerFirmConfirmProps = {
  isOpen: boolean
  title: string
  description?: ReactNode
  onConfirm: (reason: string) => void | Promise<void>
  onClose: () => void
  confirmLabel?: string
  busy?: boolean
}

export function OwnerFirmConfirm({
  isOpen,
  title,
  description,
  onConfirm,
  onClose,
  confirmLabel,
  busy = false,
}: OwnerFirmConfirmProps) {
  const { t } = useNls()
  const [reason, setReason] = useState('')
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen)
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen)
    if (isOpen) {
      setReason('')
    }
  }
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  if (!isOpen) return null

  const isReasonValid = reason.trim().length >= 12

  const handleConfirm = async () => {
    if (!isReasonValid || busy) return
    await onConfirm(reason.trim())
  }

  return (
    <div className="ownerModalOverlay" role="dialog" aria-modal="true" aria-labelledby="firm-modal-title">
      <div className="ownerModalDialog">
        <div className="ownerModalHeader">
          <h3 id="firm-modal-title">{title}</h3>
        </div>

        <div className="ownerModalBody">
          {description && <p className="ownerModalText">{description}</p>}

          <label className="ownerModalLabel">
            <span>{t('NLSOwnerConsoleReasonRequired')}</span>
            <input
              ref={inputRef}
              type="text"
              className="ownerModalInput"
              placeholder="e.g. Approved in ticket #102 for user quota recovery"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
            />
          </label>
        </div>

        <div className="ownerModalActions">
          <button
            type="button"
            className="ownerModalBtn ownerModalBtn--default"
            onClick={onClose}
            disabled={busy}
          >
            {t('NLSOwnerConsoleCancel')}
          </button>
          <button
            type="button"
            className="ownerModalBtn ownerModalBtn--primary"
            disabled={!isReasonValid || busy}
            onClick={() => void handleConfirm()}
          >
            {busy ? 'Working…' : (confirmLabel || t('NLSOwnerConsoleDoIt'))}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ==========================================================================
   3. Hard Confirm (Destructive modal, echo target human name, placeholder "…", Cancel focused)
   ========================================================================== */

export type OwnerHardConfirmProps = {
  isOpen: boolean
  title: string
  targetHumanName: string
  description?: ReactNode
  onConfirm: (reason: string) => void | Promise<void>
  onClose: () => void
  busy?: boolean
  // Phase 0d / 23 seam for Dry-Run Diff
  dryRunDiff?: ReactNode
}

export function OwnerHardConfirm({
  isOpen,
  title,
  targetHumanName,
  description,
  onConfirm,
  onClose,
  busy = false,
  dryRunDiff,
}: OwnerHardConfirmProps) {
  const { t } = useNls()
  const [typedEcho, setTypedEcho] = useState('')
  const [reason, setReason] = useState('')
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen)
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen)
    if (isOpen) {
      setTypedEcho('')
      setReason('')
    }
  }
  const cancelBtnRef = useRef<HTMLButtonElement>(null)

  const projectId = activeProjectId()
  const isProd = projectId === 'timelining-prod'
  const isDev = projectId === 'timelining-app'
  const envEcho = isProd ? 'PROD' : isDev ? 'DEV' : null

  useEffect(() => {
    if (isOpen) {
      // Default focus is on Cancel button for safety
      const timer = setTimeout(() => cancelBtnRef.current?.focus(), 50)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  if (!isOpen) return null

  const isEchoValid = typedEcho.trim() === targetHumanName.trim()
  const isReasonValid = reason.trim().length >= 12
  const canSubmit = isEchoValid && isReasonValid && !busy

  const handleConfirm = async () => {
    if (!canSubmit) return
    await onConfirm(reason.trim())
  }

  return (
    <div className="ownerModalOverlay" role="dialog" aria-modal="true" aria-labelledby="hard-modal-title">
      <div className="ownerModalDialog">
        <div className="ownerModalHeader">
          <h3 id="hard-modal-title">
            <span>{title}</span>
            {envEcho && <span className="ownerModalEnvEcho">— {envEcho}</span>}
          </h3>
        </div>

        <div className="ownerModalBody">
          {description && <p className="ownerModalText">{description}</p>}

          {/* Phase 0d / 23 Dry-run seam */}
          {dryRunDiff && (
            <div className="ownerDryRunSeam" style={{ border: '1px solid var(--warn)', borderRadius: 'var(--radius-sm)', padding: '10px' }}>
              <span style={{ fontSize: 'var(--fs-tiny)', color: 'var(--warn)', fontWeight: 700 }}>Dry Run Preview</span>
              {dryRunDiff}
            </div>
          )}

          <label className="ownerModalLabel">
            <span>
              Type <strong>{targetHumanName}</strong> to confirm:
            </span>
            <input
              type="text"
              className="ownerModalInput"
              placeholder="…" /* Rule: Placeholder is strictly "…", never the target name */
              value={typedEcho}
              onChange={(e) => setTypedEcho(e.target.value)}
              disabled={busy}
              autoComplete="off"
            />
          </label>

          <label className="ownerModalLabel">
            <span>{t('NLSOwnerConsoleReasonRequired')}</span>
            <input
              type="text"
              className="ownerModalInput"
              placeholder="e.g. Requested account closure per GDPR erasure request #48"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
            />
          </label>
        </div>

        <div className="ownerModalActions">
          <button
            ref={cancelBtnRef}
            type="button"
            className="ownerModalBtn ownerModalBtn--default"
            onClick={onClose}
            disabled={busy}
          >
            {t('NLSOwnerConsoleCancel')}
          </button>
          <button
            type="button"
            className="ownerModalBtn ownerModalBtn--danger"
            disabled={!canSubmit}
            onClick={() => void handleConfirm()}
          >
            {busy ? 'Executing…' : 'Destroy / Commit'}
          </button>
        </div>
      </div>
    </div>
  )
}
